import { useAccount, useBalance } from 'wagmi';
import { isAddress } from 'viem';
import { truncateWeiTo6Decimals } from '../../lib/web3/utils';
import { useEffect, useState, useCallback } from 'react';

import { useWeb3 } from '@/contexts/web3Provider';
import CircularProgressSpinner from '../mui/CircularProgress';

interface balanceProps {
  token?: string;
}

// 보안을 위한 최대 재시도 횟수
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY_BASE = 1000; // 1초

export default function Balance({ token }: balanceProps) {
  const { address } = useWeb3();
  const [retryCount, setRetryCount] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);

  // token이 유효한 주소 형식인지 검증
  const validToken = token && isAddress(token) ? token : undefined;

  const { data, isLoading, error, refetch } = useBalance({
    address: address as `0x${string}`,
    token: validToken,
  });

  // 보안: 재시도 로직 최적화
  const handleRetry = useCallback(async () => {
    if (retryCount < MAX_RETRY_COUNT && shouldRetry) {
      try {
        await refetch();
        setRetryCount((prev) => prev + 1);
      } catch (err) {
        console.error('잔액 조회 재시도 실패:', err);
      }
    }
  }, [retryCount, shouldRetry, refetch]);

  // 재시도 타이머 설정
  useEffect(() => {
    if (!data && !isLoading && retryCount < MAX_RETRY_COUNT && shouldRetry) {
      const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount); // 지수 백오프
      const timer = setTimeout(() => {
        console.log(`잔액 조회 재시도 ${retryCount + 1}/${MAX_RETRY_COUNT}`);
        handleRetry();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [data, isLoading, retryCount, shouldRetry, handleRetry]);

  // 컴포넌트 마운트 시 재시도 시작
  useEffect(() => {
    setShouldRetry(true);
  }, []);

  // 로딩 중
  if (isLoading) {
    return <CircularProgressSpinner size={40} />;
  }

  if (error) {
    return <span>0</span>;
  }

  // 데이터가 없는 경우
  if (!data) {
    return <span>0</span>;
  }

  // 보안: 데이터 검증
  try {
    const symbol = data.symbol || 'Unknown';
    const value = data.value?.toString() || '0';
    const decimals = data.decimals || 18;

    const formattedValue = truncateWeiTo6Decimals(value, decimals) || '0';

    return (
      <span>
        {symbol} {formattedValue}
      </span>
    );
  } catch (err) {
    console.error('잔액 포맷팅 오류:', err);
    return <span>잔액 표시 오류</span>;
  }
}
